import React, { useState, useEffect, useCallback, useRef } from "react";
import { DataProvider, useData } from "../../context/DataContext.jsx";
import { openUnlessCancelled } from "../../lib/quotePersistence.js";
import { DocProvider } from "../../context/DocContext.jsx";
import { dataService } from "../../lib/dataService.js";
import notify from "../../lib/notify.jsx";
import { C, font } from "../../lib/styles.js";
import JobsList from "./views/JobsList.jsx";
import JobDetailStub from "./views/JobDetailStub.jsx";
import Dashboard from "./views/Dashboard.jsx";
import ScheduleView from "./views/ScheduleView.jsx";
import SavedQuotes from "./views/SavedQuotes.jsx";
import QuoteBuilder from "./views/QuoteBuilder.jsx";
import Insights from "./views/Insights.jsx";
import { jobAccess } from "./lib/jobAccess.js";
import { usePageIds } from "../../lib/feedback/pageIds.js";
import { takePendingJob } from "../../lib/feedback/deepLink.js";

// Fallback only — App.jsx passes the real (access-filtered) nav list as a prop.
const DEFAULT_TABS = ["Dashboard", "Schedule", "Quotes", "Jobs"];

export default function JobsApp({ activeTab = 0, navClickTick = 0, user, navItems, setActiveTab, pendingConvertId = null, onConvertConsumed }) {
  // Role-aware data layer (holds slice 2): non-admin sessions load and
  // subscribe to jobs, holds, and the studio settings only; quotes, contacts,
  // clients, and the rest of the admin data are never requested. Keyed on the
  // role so a role change remounts the provider with fresh listeners and
  // closures instead of mixing two roles' state.
  const role = jobAccess(user?.role).role;
  return (
    <DataProvider key={role} role={role}>
      {/* Docs are admin-only surfaces — crew/viewer sessions skip the
          docs/contentBlocks Firestore traffic entirely. */}
      <DocProvider enabled={jobAccess(user?.role).isAdmin}>
        <JobsAppInner activeTab={activeTab} navClickTick={navClickTick} user={user} navItems={navItems} setActiveTab={setActiveTab} pendingConvertId={pendingConvertId} onConvertConsumed={onConvertConsumed} />
      </DocProvider>
    </DataProvider>
  );
}

function JobsAppInner({ activeTab, navClickTick, user, navItems, setActiveTab, pendingConvertId, onConvertConsumed }) {
  // Route off the SAME (already access-filtered) tab list the topbar renders,
  // never a static array. If these two lists ever diverged, hiding a tab would
  // shift positions and misroute the survivors (e.g. "Jobs" opening Quotes).
  const tabs = (navItems && navItems.length) ? navItems : DEFAULT_TABS;
  const access = jobAccess(user?.role);
  // A feedback inbox link (?go=jobs&tab=Jobs&job=<id>, src/lib/feedback/deepLink.js) opens on that job.
  const [activeJobId, setActiveJobId] = useState(() => takePendingJob("jobs"));
  const label = tabs[activeTab] || tabs[0] || "Unknown";
  usePageIds("jobs", { jobId: label === "Jobs" ? activeJobId : null });

  // Any topbar nav click (including the already-active tab) returns Jobs to its
  // list view. Not on mount (the tick it mounted with), so a linked job stays open.
  const mountTick = useRef(navClickTick);
  useEffect(() => {
    if (navClickTick === mountTick.current) return;
    mountTick.current = navClickTick;
    setActiveJobId(null);
  }, [navClickTick]);

  // Deep-linked prospect conversion (?convert= from the inquiry email or the
  // Prospects panel): once data is hydrated, fetch the submission, run the
  // transactional conversion, and land in the builder. The callbacks change
  // identity as builder data changes, so keep their latest values outside the
  // effect dependencies; otherwise dependency cleanup can abandon an in-flight
  // conversion while the claim prevents its replacement from retrying.
  const { startQuoteFromProspect, loaded } = useData();
  const claimedConvertRef = useRef(null);
  const convertCallbacksRef = useRef({ startQuoteFromProspect, onConvertConsumed });
  convertCallbacksRef.current = { startQuoteFromProspect, onConvertConsumed };
  useEffect(() => {
    if (!pendingConvertId) {
      claimedConvertRef.current = null;
      return;
    }
    if (!loaded) return;
    if (claimedConvertRef.current === pendingConvertId) return;
    claimedConvertRef.current = pendingConvertId;
    let unmounted = false;
    (async () => {
      try {
        const raw = await dataService.quoteSubmissions.get(pendingConvertId);
        if (unmounted) { claimedConvertRef.current = null; return; }
        if (!raw) {
          notify.error("Couldn't find that inquiry to convert.");
          convertCallbacksRef.current.onConvertConsumed?.();
          return;
        }
        await convertCallbacksRef.current.startQuoteFromProspect(raw);
        if (unmounted) { claimedConvertRef.current = null; return; }
        // The builder opens either way: on the converted inquiry, or, when the
        // replacement was cancelled, on the quote that is still open with its edits.
        setPendingBuilder(true);
        convertCallbacksRef.current.onConvertConsumed?.();
      } catch (e) {
        console.error("Prospect conversion failed:", e);
        if (!unmounted) {
          notify.error("Couldn't convert that inquiry. Try the link again.");
          convertCallbacksRef.current.onConvertConsumed?.();
        }
        claimedConvertRef.current = null;
      }
    })();
    return () => { unmounted = true; };
  }, [pendingConvertId, loaded]);

  // Safety backstop for the money rule: a stale activeTab must never render the
  // Quotes (pricing) screen for someone without the capability. The menu is
  // already filtered upstream, so this only fires on a stale index.
  useEffect(() => {
    if (label === "Quotes" && !access.canUseQuotes) setActiveTab?.(0);
  }, [label, access.canUseQuotes, setActiveTab]);

  // Same backstop for Insights (revenue analytics): a stale activeTab index must
  // never render money data for a role without canSeeMoney. Menu is filtered
  // upstream, so this only fires on a stale index.
  useEffect(() => {
    if (label === "Insights" && !access.canSeeMoney) setActiveTab?.(0);
  }, [label, access.canSeeMoney, setActiveTab]);

  const openJob = useCallback((id) => setActiveJobId(id), []);
  const openBookedJob = useCallback((job) => {
    if (!job?.id) return;
    setActiveJobId(job.id);
    const jobsIdx = tabs.findIndex(t => t === "Jobs");
    if (jobsIdx !== -1 && jobsIdx !== activeTab) setActiveTab?.(jobsIdx);
  }, [activeTab, setActiveTab, tabs]);

  // Jump from a job's Financials tab straight into the Quote Builder (amend /
  // change order / related quote — the quote is already loaded into builder
  // state by DataContext before this fires). The flag makes QuotesWorkspace
  // mount in builder mode once, then clears; if Quotes is hidden for this role
  // the findIndex misses and this no-ops, same as navigate().
  const [pendingBuilder, setPendingBuilder] = useState(false);
  const openBuilder = useCallback(() => {
    const qIdx = tabs.findIndex(t => t === "Quotes");
    if (qIdx === -1) return;
    setPendingBuilder(true);
    if (qIdx !== activeTab) setActiveTab?.(qIdx);
  }, [activeTab, setActiveTab, tabs]);
  const consumePendingBuilder = useCallback(() => setPendingBuilder(false), []);

  // Cross-tab navigation for sub-views. Dashboard uses this to open a job detail or jump to Quotes.
  // Calls setActiveTab directly (not handleNavChange) so navClickTick stays put and activeJobId survives.
  // findIndex runs against the filtered `tabs`, so a hidden target (e.g. Quotes for a non-admin) no-ops.
  const navigate = useCallback((target, id) => {
    const targetIdx = tabs.findIndex(t => t.toLowerCase() === String(target).toLowerCase());
    if (targetIdx === -1) return;
    if (id) setActiveJobId(id);
    if (targetIdx !== activeTab) setActiveTab?.(targetIdx);
  }, [activeTab, setActiveTab, tabs]);

  if (label === "Jobs") {
    return activeJobId
      ? <JobDetailStub jobId={activeJobId} user={user} onOpenJob={openJob} onOpenBuilder={openBuilder} />
      : <JobsList user={user} onOpenJob={openJob} />;
  }

  if (label === "Dashboard") return <Dashboard navigate={navigate} user={user} />;

  // Every role: holds and jobs on a month grid, with the crew rules inside the view.
  if (label === "Schedule") return <ScheduleView navigate={navigate} user={user} />;

  if (label === "Insights") {
    // Hard block on mount — mirrors the Quotes guard below. The upstream nav
    // filter + the effect above keep non-admins out; this guarantees revenue
    // analytics never render regardless of how the tab index got stale.
    if (!access.canSeeMoney) return null;
    return <Insights />;
  }

  if (label === "Quotes") {
    // Hard block on mount — the upstream filter + the effect above should keep
    // us out of here without the capability; this guarantees the pricing UI
    // never renders regardless of how the index got stale.
    if (!access.canUseQuotes) return null;
    // While a deep-linked conversion is pending, hold the workspace back:
    // QuotesWorkspace reads initialMode once at mount, so it must not mount in
    // "saved" mode before the conversion sets pendingBuilder (and the operator
    // must not be able to click New Quote mid-conversion).
    if (pendingConvertId) {
      return <div style={styles.placeholder}>Converting inquiry to a quote…</div>;
    }
    return <QuotesWorkspace onOpenJob={openBookedJob} initialMode={pendingBuilder ? "builder" : "saved"} onConsumeInitialMode={consumePendingBuilder} />;
  }

  return <div style={styles.placeholder}>Unknown jobs view.</div>;
}

function QuotesWorkspace({ onOpenJob, initialMode = "saved", onConsumeInitialMode }) {
  const { loadQuote, loadDraft, newQuote, duplicateQuote } = useData();
  // initialMode is read once at mount (a Financials-tab jump lands directly in
  // the builder); the consume callback clears the parent flag so a later
  // visit to Quotes opens the list as usual.
  const [mode, setMode] = useState(initialMode);
  useEffect(() => { onConsumeInitialMode?.(); }, [onConsumeInitialMode]);
  usePageIds("jobs-quotes", { mode });

  // Every way into the builder replaces the open quote first (lib/quotePersistence.js).
  // When that stops (Cancel in the Unsaved dialog, a save still running), the builder
  // opens on the quote that is still there, so the edits the dialog kept can be seen.
  const showBuilder = useCallback(() => setMode("builder"), []);
  const createQuote = useCallback(async () => {
    await openUnlessCancelled(() => newQuote?.(), showBuilder, showBuilder);
  }, [newQuote, showBuilder]);

  const editQuote = useCallback(async (quote) => {
    if (!quote) return;
    // A draft reopens via the draft loader (savedToDb:false, attachment normalize);
    // an ingested quote loads through loadQuote.
    await openUnlessCancelled(() => (quote.draft ? loadDraft?.(quote.id) : loadQuote?.(quote)), showBuilder, showBuilder);
  }, [loadQuote, loadDraft, showBuilder]);

  // Duplicate loads a fresh-id copy (status reset to quoted) into builder state.
  const dupQuote = useCallback(async (quote) => {
    if (!quote) return;
    await openUnlessCancelled(() => duplicateQuote?.(quote), showBuilder, showBuilder);
  }, [duplicateQuote, showBuilder]);

  if (mode === "builder") {
    return (
      <QuoteBuilder
        onSaved={() => setMode("saved")}
        onOpenSaved={() => setMode("saved")}
      />
    );
  }

  return (
    <SavedQuotes
      onOpenJob={onOpenJob}
      onCreateQuote={createQuote}
      onEditQuote={editQuote}
      onDuplicateQuote={dupQuote}
    />
  );
}

const styles = {
  placeholder: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: 32,
    textAlign: "center",
    color: C.muted,
    fontSize: 13,
    fontWeight: 300,
    fontFamily: font,
  },
};
